import { useEffect, useMemo } from "react";

import { useHubPanelStore } from "@/expert-hub/right-panel/useHubPanelStore";

// Off-call the right panel is a 30% rail beside the 70% Expert Hub.
const OFF_CALL_PANEL_SIZE = "30%";
// Off-call sizing band: a narrower floor than the on-call default so the hub's 3-col grid isn't
// squished on small laptops (at 440px the panel eats ~43% of a 1024px screen), plus a hard cap so
// the panel doesn't sprawl on wide monitors. On-call keeps the layout's own min and no cap.
const OFF_CALL_PANEL_MIN = "360px";
const OFF_CALL_PANEL_MAX = "480px";

interface WorkspaceHomeStateParams {
    /** No active call/chat (and not in an Expert University / training takeover). */
    isOffCall: boolean;
    /** Whether the on-call right panel would be open (e.g. `!expertUniversityActive` / `canShowChatDetails`). */
    baseRightTopOpen: boolean;
}

interface WorkspaceHomeState {
    /** True when the Expert Hub should occupy the main area (i.e. off-call in the v2 workspace). */
    showExpertHub: boolean;
    /** Effective right-top open state: the off-call hub panel off-call, the on-call panel otherwise. */
    rightTopOpen: boolean;
    /** Right-top target width: a 30% rail off-call, default sizing on-call. */
    rightTopSize?: string;
    /** Off-call min/max width band for the hub rail; undefined on-call (layout default min, no cap). */
    rightTopMin?: string;
    rightTopMax?: string;
}

/**
 * Shared off-call↔on-call state for the redesigned voice/chat workspaces. Owns the Expert Hub gate
 * and the off-call panel's default-closed / 70-30 behavior, so voice and chat stay in lockstep
 * instead of hand-syncing the same logic. The off-call panel resets to closed on going on-call, so it
 * defaults back to closed when the expert returns off-call.
 */
export function useWorkspaceHomeState({ isOffCall, baseRightTopOpen }: WorkspaceHomeStateParams): WorkspaceHomeState {
    // v2 is the Expert Hub workspace, so being off-call is the only gate — the hub is the home.
    const showExpertHub = isOffCall;
    const hubPanelOpen = useHubPanelStore((s) => s.open);
    const closeHubPanel = useHubPanelStore((s) => s.closePanel);

    // On going on-call, reset the off-call panel to closed so it defaults back to closed when the
    // expert returns off-call.
    useEffect(() => {
        if (!showExpertHub && hubPanelOpen) {
            closeHubPanel();
        }
    }, [showExpertHub, hubPanelOpen, closeHubPanel]);

    const rightTopOpen = showExpertHub ? hubPanelOpen : baseRightTopOpen;

    return useMemo(
        () => ({
            showExpertHub,
            rightTopOpen,
            rightTopSize: showExpertHub ? OFF_CALL_PANEL_SIZE : undefined,
            rightTopMin: showExpertHub ? OFF_CALL_PANEL_MIN : undefined,
            rightTopMax: showExpertHub ? OFF_CALL_PANEL_MAX : undefined,
        }),
        [showExpertHub, rightTopOpen],
    );
}
